/**
 * Financing quotes for one vehicle from POST /api/financing/calculate, for the
 * financing calculator UI. See docs/santander-financing.md ("Frontend
 * implementation") for the rules this follows.
 */
import type { FetchError } from 'ofetch';
import type { FinancingFieldError, FinancingInput, Quote } from '#shared/financing';

// After a choice or keystroke, wait this long before asking, so clicking through options sends only the last.
const PAUSE_MS = 300;
// A Retry-After up to this long is waited out by the composable, showing "loading".
const AUTO_RETRY_MAX_SECONDS = 5;
const AUTO_RETRY_ATTEMPTS = 3;

export type QuoteState =
  | { status: 'idle' | 'loading' }
  | { status: 'ready'; quote: Quote }
  | { status: 'terms-rejected'; errors: FinancingFieldError[] }
  /** No quote for this vehicle: hide the calculator, offer the inquiry form. */
  | { status: 'not-financeable' }
  /** Busy, not configured or failing; retryAfterSeconds when the server said how long. */
  | { status: 'unavailable'; retryAfterSeconds?: number }
  /** The server wants a (fresh) Turnstile check before it calculates. */
  | { status: 'needs-turnstile' };

export function useFinancingQuote(vehicleId: MaybeRefOrGetter<string>) {
  const state = ref<QuoteState>({ status: 'idle' });
  // Answers that will not change during the visit; going back to a combination costs nothing.
  const answers = new Map<string, QuoteState>();
  let inFlight: AbortController | undefined;
  let retryTimer: ReturnType<typeof setTimeout> | undefined;

  // Turnstile: the widget's token (v-model) is verified with the existing flow,
  // then the calculation the server refused is repeated.
  const { verifyTurnstileToken } = useValidTurnstileTokenCookie();
  const turnstileToken = ref('');
  // Bound to the widget's :key; a new key re-renders it with a fresh challenge.
  const turnstileKey = ref(0);
  let refused: FinancingInput | undefined;
  watch(turnstileToken, async token => {
    if (!token) return;
    if (!(await verifyTurnstileToken(token))) {
      turnstileKey.value++;
      return;
    }
    if (refused) await calculate(refused);
  });

  function calculate(input: FinancingInput) {
    return ask(input, 0);
  }

  async function ask(input: FinancingInput, retries: number) {
    // A newer input supersedes the request still in flight.
    inFlight?.abort();
    clearTimeout(retryTimer);

    const body = { vehicleId: toValue(vehicleId), ...input };
    const key = JSON.stringify(body);
    const known = answers.get(key);
    if (known) {
      state.value = known;
      return;
    }

    const { signal } = (inFlight = new AbortController());
    state.value = { status: 'loading' };
    try {
      const quote = await $fetch<Quote>('/api/financing/calculate', { method: 'POST', body, signal });
      state.value = { status: 'ready', quote };
    } catch (error) {
      if (signal.aborted) return;
      const answer = toQuoteState(error as FetchError);
      const wait = answer.status === 'unavailable' ? answer.retryAfterSeconds : undefined;
      if (wait && wait <= AUTO_RETRY_MAX_SECONDS && retries < AUTO_RETRY_ATTEMPTS) {
        // Busy for a moment (e.g. Santander's per-second limit): wait it out.
        retryTimer = setTimeout(() => ask(input, retries + 1), wait * 1000);
        return;
      }
      state.value = answer;
      if (state.value.status === 'needs-turnstile') refused = input;
    }
    if (state.value.status === 'ready' || state.value.status === 'terms-rejected') {
      answers.set(key, state.value);
    }
  }

  return {
    state: readonly(state),
    /** Render <NuxtTurnstile :key="turnstileKey" v-model="turnstileToken"> while true. */
    needsTurnstile: computed(() => state.value.status === 'needs-turnstile'),
    turnstileToken,
    turnstileKey: readonly(turnstileKey),
    /** Asks right away: opening the calculator, retrying. */
    calculate,
    /** Asks once the visitor pauses: choices (buttons, selects, slider release) and typed amounts. */
    calculateDebounced: useDebounceFn(calculate, PAUSE_MS),
  };
}

/** Maps the endpoint's error responses (h3: { statusCode, data: { reason, errors? } }). */
function toQuoteState(error: FetchError): QuoteState {
  const data = error.data?.data;
  switch (error.statusCode) {
    case 403: // no valid Turnstile cookie
      return { status: 'needs-turnstile' };
    case 429: // this token's limit is spent; a fresh check renews it
      return data?.reason === 'visitor-limit' ? { status: 'needs-turnstile' } : { status: 'unavailable' };
    case 404:
      return { status: 'not-financeable' };
    case 422:
      return data?.reason === 'terms-rejected' ? { status: 'terms-rejected', errors: data.errors } : { status: 'not-financeable' };
    default: {
      const retryAfterSeconds = Number(error.response?.headers.get('retry-after'));
      return retryAfterSeconds ? { status: 'unavailable', retryAfterSeconds } : { status: 'unavailable' };
    }
  }
}
